import { firebaseAuth } from './firebase'
import { propertyFetch } from './property-api'

type Row = Record<string, unknown>
type Constraint = { field?: string; op?: string; value?: unknown; order?: string; count?: number; params?: Record<string, string | number> }
type Collection = { kind: 'collection'; name: string; constraints: Constraint[] }
type Document = { kind: 'document'; name: string; id: string }
type SnapshotRow = { id: string; data: () => Row; exists: () => boolean }
type PageMeta = { nextOffset: number | null; total?: number; summary?: Record<string, unknown> }
type Snapshot = SnapshotRow & PageMeta & { docs: SnapshotRow[]; empty: boolean; size: number }
export const propertyDb = Object.freeze({})
export const collection = (_db: typeof propertyDb, name: string): Collection => ({ kind: 'collection', name, constraints: [] })
export const doc = (_db: typeof propertyDb, name: string, id: string): Document => ({ kind: 'document', name, id })
export const where = (field: string, op: string, value: unknown): Constraint => ({ field, op, value })
export const orderBy = (field: string, order = 'asc'): Constraint => ({ field, order })
export const limit = (count: number): Constraint => ({ count })
export const pageOptions = (params: Record<string, string | number>): Constraint => ({ params })
export const query = (reference: Collection, ...constraints: Constraint[]): Collection => ({ ...reference, constraints: [...reference.constraints, ...constraints] })
export const serverTimestamp = () => new Date().toISOString()
export const deleteField = () => null

const refreshers = new Set<() => void>()
const inFlight = new Map<string, Promise<Row[]>>()
function ownDocument(reference: Document) {
  if (reference.id !== firebaseAuth.currentUser?.uid) throw new Error('Akses data akun ditolak.')
}
async function pages(path: string, publicAccess = false): Promise<Row[]> {
  const key = `${publicAccess ? 'public' : firebaseAuth.currentUser?.uid}:${path}`
  const current = inFlight.get(key)
  if (current) return current
  const request = (async () => {
    const data = await propertyFetch<{ items: Row[] } & PageMeta>(path, { public: publicAccess })
    return Object.assign(data.items, { nextOffset: data.nextOffset ?? null, total: data.total, summary: data.summary })
  })()
  inFlight.set(key, request)
  try { return await request } finally { inFlight.delete(key) }
}
async function load(reference: Collection | Document): Promise<Row[]> {
  if (reference.kind === 'document') {
    if (reference.name === 'propertyUsers' || reference.name === 'propertyAiCreditWallets') {
      ownDocument(reference)
      const { item } = await propertyFetch<{ item: Row | null }>(reference.name === 'propertyUsers' ? '/v1/property/me/profile' : '/v1/property/me/ai-wallet')
      return item ? [item] : []
    }
    if (reference.name === 'propertyListings') {
      try {
        const { item } = await propertyFetch<{ item: Row }>(`/v1/property/listings/${encodeURIComponent(reference.id)}`, { public: true })
        return [item]
      } catch (error) {
        if ((error as { status?: number }).status === 404) return []
        throw error
      }
    }
    throw new Error('Resource tidak tersedia.')
  }
  const params = new URLSearchParams({ limit: '20', offset: '0' })
  for (const constraint of reference.constraints) {
    const { field, op, value, order, count } = constraint
    if (constraint.params) Object.entries(constraint.params).forEach(([key,value])=>params.set(key,String(value)))
    if (count) params.set('limit',String(count))
    if (field && op) {
      if (['ownerUid','uid','ownerEmail'].includes(field)) {
        const expected = field==='ownerEmail' ? firebaseAuth.currentUser?.email : firebaseAuth.currentUser?.uid
        if (op!=='==' || value!==expected) throw new Error('Akses data akun ditolak.')
      } else if (op==='==') params.set(field,String(value))
      else throw new Error('Filter tidak tersedia.')
    }
    if (field && order) {
      if(field==='createdAt' && order==='desc') params.set('sort','created_desc')
      else throw new Error('Urutan tidak tersedia.')
    }
  }
  const request = (path: string, publicAccess=false) => pages(`${path}?${params}`,publicAccess)
  switch (reference.name) {
    case 'propertyUsers': return request('/v1/property/agents', true)
    case 'propertyListings': {
      const mine = reference.constraints.some(item => item.field === 'ownerUid' || item.field === 'ownerEmail')
      return request(mine ? '/v1/property/me/listings' : '/v1/property/listings', !mine)
    }
    case 'propertyAnalyticsEvents': return request('/v1/property/me/analytics')
    case 'propertyAiImageJobs': return request('/v1/property/me/image-jobs')
    case 'propertyPayments': return request('/v1/property/me/payments')
    case 'propertyAgentBillingHistories': params.set('kind','agent_subscription'); return request('/v1/property/me/payments')
    case 'propertyFeaturedBillingHistories': params.set('kind','featured_listing'); return request('/v1/property/me/payments')
    case 'propertyAiCreditTopups': params.set('kind','ai_credit_topup'); return request('/v1/property/me/payments')
    default: throw new Error('Resource tidak tersedia.')
  }
}
function snapshot(rows: Row[]): Snapshot {
  const docs = rows.map(row => ({ id: String(row.id), data: () => row, exists: () => true }))
  const meta = rows as Row[] & Partial<PageMeta>
  return { id: docs[0]?.id || '', data: () => docs[0]?.data() || {}, exists: () => docs.length > 0, docs, empty: docs.length === 0, size: docs.length, nextOffset: meta.nextOffset ?? null, total: meta.total, summary: meta.summary }
}
export async function getDocs(reference: Collection) { return snapshot(await load(reference)) }
export function onSnapshot(reference: Collection | Document, next: (snapshot: Snapshot) => void, error?: (error: Error) => void) {
  let stopped = false, busy = false, timer: ReturnType<typeof setTimeout> | undefined
  const initialUid = firebaseAuth.currentUser?.uid
  const publicRead = reference.kind === 'document' ? reference.name === 'propertyListings'
    : reference.name === 'propertyUsers' || (reference.name === 'propertyListings' && !reference.constraints.some(item => item.field === 'ownerUid' || item.field === 'ownerEmail'))
  const refresh = async () => {
    if (stopped || busy) return
    busy = true
    try {
      const rows = await load(reference)
      if (!stopped && (publicRead || firebaseAuth.currentUser?.uid === initialUid)) next(snapshot(rows))
    } catch (failure) { if (!stopped) error?.(failure instanceof Error ? failure : new Error('Data belum berhasil dimuat.')) }
    finally {
      busy = false
      if (!stopped) { clearTimeout(timer); timer = setTimeout(() => void refresh(), 20000) }
    }
  }
  const trigger = () => void refresh()
  refreshers.add(trigger)
  trigger()
  return () => { stopped = true; clearTimeout(timer); refreshers.delete(trigger) }
}
function changed() { for (const refresh of refreshers) refresh() }
export async function setDoc(reference: Document, data: Row, _options?: { merge?: boolean }) {
  if (_options?.merge === false) throw new Error('Penggantian profil penuh tidak tersedia.')
  if (reference.name !== 'propertyUsers') throw new Error('Resource tidak dapat diubah.')
  ownDocument(reference)
  const fields = ['fullName', 'phoneNumber', 'photoUrl', 'companyName', 'companyLogoUrl', 'accountType']
  await propertyFetch('/v1/property/me/profile', { method: 'PUT', body: Object.fromEntries(fields.map(field => [field, data[field]])) })
  changed()
}
export async function addDoc(reference: Collection, data: Row) {
  let result: { id: string }
  if (reference.name === 'propertyListings') result = await propertyFetch('/v1/property/me/listings', { method: 'POST', body: data })
  else if (reference.name === 'propertyListingReports') result = await propertyFetch(`/v1/property/listings/${encodeURIComponent(String(data.listingId))}/reports`, { method: 'POST', body: data, public: true })
  else throw new Error('Resource tidak dapat dibuat.')
  changed()
  return { id: result.id }
}
export async function updateDoc(reference: Document, data: Row) {
  if (reference.name !== 'propertyListings') throw new Error('Resource tidak dapat diubah.')
  const path = `/v1/property/me/listings/${encodeURIComponent(reference.id)}`
  if (data.pendingVersion || data.title !== undefined) await propertyFetch(path, { method: 'PATCH', body: data })
  else {
    const action = data.renewalRequestedAt ? 'renew' : data.status === 'pending_review' ? 'submit-review' : data.status === 'sold' ? 'sold' : data.status === 'inactive' ? 'hide' : data.status === 'verified' ? 'reactivate' : ''
    if (!action) throw new Error('Perubahan status tidak tersedia.')
    await propertyFetch(`${path}/transitions`, { method: 'POST', body: { action } })
  }
  changed()
}
